import React, { useRef, useState, useCallback } from 'react';
import { Box } from '@mui/material';

/**
 * Shows a thumbnail image, and on mouse hover plays the actual video.
 * Display uses object-fit: cover (square crop by shorter side).
 *
 * Props:
 *   thumbnailSrc - URL to the preview image
 *   videoSrc     - URL to the actual video file
 *   sx           - additional sx props for the container Box
 */
export default function VideoHoverPreview({ thumbnailSrc, videoSrc, sx }) {
  const videoRef = useRef(null);
  const [hovering, setHovering] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setHovering(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, []);

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...sx
      }}
    >
      {/* Thumbnail - always rendered, hidden when hovering */}
      {thumbnailSrc && (
        <Box
          component="img"
          src={thumbnailSrc}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: hovering && videoSrc ? 0 : 1,
            transition: 'opacity 0.2s'
          }}
        />
      )}
      {/* Video - preloaded on metadata, plays on hover */}
      {videoSrc && (
        <Box
          component="video"
          ref={videoRef}
          src={videoSrc}
          muted
          loop
          playsInline
          preload="metadata"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: hovering ? 1 : 0,
            transition: 'opacity 0.2s'
          }}
        />
      )}
    </Box>
  );
}
